"use client";

import { Button } from "@/app/components/ui/button";
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { Flex } from "@radix-ui/themes";
import { useSearchParams } from "next/navigation";
import React, { Fragment, useContext } from "react";
import { PaginationEllipsis } from "../components/ui/pagination";
import { cn } from "@/lib/utils";
import { AuctionFilterContext } from "@/lib/context/AuctionFilterContext";
import { AUCTION_CATALOGUE_DEFAULT_PAGE_SIZE } from "@/lib/constants";
export interface PaginationProps {
  className?: string;
}

const LotPagination = ({
  className,
}: PaginationProps) => {
  const searchParams = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1');
  const pageSize = parseInt(searchParams.get('pageSize') || AUCTION_CATALOGUE_DEFAULT_PAGE_SIZE.toString());

  const auctionFilterContext = useContext(AuctionFilterContext);
  const itemCount = auctionFilterContext?.itemCount || 0;

  const pageCount = Math.ceil(itemCount / pageSize);
  if (pageCount <= 1) return null;

  const changePage = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", page.toString());
    window.history.pushState({}, '', '?' + params.toString());
    // Scroll to the top of the page or to a specific component if needed
    // You can use a ref to scroll to a specific component
    // Example: if (componentRef.current) window.scrollTo({ top: componentRef.current.offsetTop, behavior: 'smooth' });
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const coerce = (page: number) => {
    if (page < 1) return 1;
    if (page > pageCount) return pageCount;
    return page;
  };

  const pageWindow = [
    currentPage - 1,
    currentPage,
    currentPage + 1,
    currentPage + 2,
  ].map(coerce);
  const numberedPages = [...new Set([...pageWindow, 1, pageCount])].sort(
    (a, b) => a - b
  );

  const NumberedButtons = () => {
    return (
      <Flex>
        {numberedPages.map((page: number, index: number) => {
          // Check if the next page number has a gap larger than 1
          const isGap =
            index < numberedPages.length - 1 &&
            numberedPages[index + 1] - page > 1;

          return (
            <Fragment key={index}>
              <Button
                onClick={() => changePage(page)}
                variant={currentPage === page ? "outline" : "ghost"}
                className="text-xs w-5"
              >
                {page}
              </Button>
              {isGap && <PaginationEllipsis />}
            </Fragment>
          );
        })}
      </Flex>
    );
  };

  return (
    <Flex className={cn("w-min", className)} align="center">
      <Flex align="center" gap="2">
        <Button
          color="gray"
          variant="ghost"
          disabled={currentPage === 1}
          onClick={() => changePage(currentPage - 1)}
          className="text-xs"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          <span>Previous</span>
        </Button>
        <NumberedButtons />
        <Button
          color="gray"
          variant="ghost"
          disabled={currentPage === pageCount}
          onClick={() => changePage(currentPage + 1)}
          className="text-xs"
        >
          <span>Next</span>
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </Flex>
    </Flex>
  );
};

export default LotPagination;
